/* eslint-disable spellcheck/spell-checker */

export type GenreWithoutCurators = {
  genreName: string
  id?: string
}

export type PredefinedGenres = {
  subTitle: string
  genres: GenreWithoutCurators[]
}

export interface MergedGenres {
  predefinedGenres: PredefinedGenres[]
}

export const PREDEFINED_GENRES = [
  {
    subTitle: 'Pop',
    genres: [
      'Mainstream Pop',
      'Teen Pop',
      'Dance Pop',
      'Electropop',
      'Synth-pop',
      'Power Pop',
      'Bubblegum Pop',
      'Adult Contemporary',
      'K-Pop',
      'J-Pop',
      'Latin Pop',
      'Indie Pop',
      'Art Pop',
      'Chamber Pop',
      'Baroque Pop',
    ],
  },
  {
    subTitle: 'Rock',
    genres: [
      'Classic Rock',
      'Hard Rock',
      'Soft Rock',
      'Punk Rock',
      'Pop Punk',
      'Post-Punk',
      'Hardcore Punk',
      'Alternative Rock',
      'Grunge',
      'Britpop',
      'Shoegaze',
      'Dream Pop',
      'Indie Rock',
      'Post-Rock',
      'Metal',
      'Heavy Metal',
      'Death Metal',
      'Black Metal',
      'Doom Metal',
      'Thrash Metal',
      'Nu Metal',
      'Progressive Metal',
      'Metalcore',
      'Progressive Rock',
      'Psychedelic Rock',
      'Folk Rock',
      'Blues Rock',
      'Garage Rock',
    ],
  },
  {
    subTitle: 'Hip-Hop/Rap',
    genres: [
      'Old School Hip-Hop',
      'Gangsta Rap',
      'Trap',
      'Drill',
      'Alternative Hip-Hop',
      'Conscious Hip-Hop',
      'Mumble Rap',
      'Boom Bap',
      'Grime',
      'Lo-fi Hip-Hop',
    ],
  },
  {
    subTitle: 'R&B/Soul',
    genres: [
      'Contemporary R&B',
      'Neo-Soul',
      'Classic Soul',
      'Funk',
      'Motown',
      'Gospel',
      'Alternative R&B',
    ],
  },
  {
    subTitle: 'Electronic/Dance',
    genres: [
      'House',
      'Deep House',
      'Progressive House',
      'Tech House',
      'Tropical House',
      'Electro House',
      'Techno',
      'Trance',
      'Dubstep',
      'Drum and Bass',
      'Ambient',
      'IDM (Intelligent Dance Music)',
      'Electronica',
      'Breakbeat',
      'UK Garage',
      'Hardstyle',
      'EDM (Electronic Dance Music)',
    ],
  },
  {
    subTitle: 'Country',
    genres: [
      'Traditional Country',
      'Country Pop',
      'Country Rock',
      'Outlaw Country',
      'Bluegrass',
      'Americana',
      'Alternative Country',
      'Country Folk',
      'Western Swing',
    ],
  },
  {
    subTitle: 'Jazz',
    genres: [
      'Bebop',
      'Swing',
      'Cool Jazz',
      'Modal Jazz',
      'Free Jazz',
      'Fusion',
      'Smooth Jazz',
      'Latin Jazz',
      'Nu Jazz',
    ],
  },
  {
    subTitle: 'Blues',
    genres: [
      'Delta Blues',
      'Chicago Blues',
      'Electric Blues',
      'Jump Blues',
      'Blues Rock',
      'Soul Blues',
      'Contemporary Blues',
    ],
  },
  {
    subTitle: 'Folk',
    genres: [
      'Traditional Folk',
      'Contemporary Folk',
      'Folk Rock',
      'Indie Folk',
      'Freak Folk',
      'Folk Pop',
      'Celtic',
      'Americana',
    ],
  },
  {
    subTitle: 'World Music',
    genres: [
      'Latin',
      'Salsa',
      'Reggaeton',
      'Bachata',
      'Cumbia',
      'Merengue',
      'Bossa Nova',
      'Samba',
      'African',
      'Afrobeat',
      'Highlife',
      'Soukous',
      'Mbalax',
      'Caribbean',
      'Reggae',
      'Dancehall',
      'Calypso',
      'Soca',
      'Middle Eastern',
      'Indian/South Asian',
      'Celtic',
      'Flamenco',
    ],
  },
  {
    subTitle: 'Classical',
    genres: [
      'Baroque',
      'Classical Period',
      'Romantic',
      'Contemporary Classical',
      'Minimalist',
      'Opera',
      'Chamber Music',
      'Orchestral',
      'Choral',
    ],
  },
  {
    subTitle: 'Reggae/Dub',
    genres: [
      'Roots Reggae',
      'Dub',
      'Dancehall',
      'Reggae Fusion',
      'Ska',
      'Rocksteady',
    ],
  },
  {
    subTitle: 'Experimental/Avant-garde',
    genres: [
      'Noise',
      'Industrial',
      'Musique Concrète',
      'Sound Art',
      'Free Improvisation',
    ],
  },
  {
    subTitle: 'Funk/Disco',
    genres: ['P-Funk', 'Disco', 'Boogie', 'Nu-Disco', 'Funk Rock'],
  },
  {
    subTitle: 'New Age',
    genres: [
      'Ambient',
      'Meditation',
      'Spiritual',
      'Environmental',
      'Space Music',
    ],
  },
  {
    subTitle: "Children's Music",
    genres: ['Educational', 'Lullabies', 'Action Songs', 'Nursery Rhymes'],
  },
  {
    subTitle: 'Comedy/Spoken Word',
    genres: [
      'Stand-up Comedy',
      'Spoken Word Poetry',
      'Audio Books',
      'Comedy Songs',
    ],
  },
  {
    subTitle: 'Fusion Genres',
    genres: [
      'Jazz Fusion',
      'Rock Fusion',
      'World Fusion',
      'Electro Swing',
      'Trip-Hop',
      'Nu Metal',
      'Folktronica',
    ],
  },
] as const

export const mergeCampaignGenres = (): MergedGenres => ({
  predefinedGenres: PREDEFINED_GENRES.map((item) => ({
    ...item,
    genres: (item?.genres ?? []).map((genreName) => ({
      id: genreName,
      key: `${item.subTitle}-${genreName}`.replace(/\s+/g, '-').toLowerCase(),
      genreName,
    })) as unknown as GenreWithoutCurators[],
  })),
})
